import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PredictedConditionsDraft,
  TrackConditionsDraft,
  usePredictedConditions,
  useSavePredictedConditions,
  useSaveTrackConditions,
  useTrackConditions,
} from '../../queries/meetings';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/Button';
import { useN8nWebhook } from '../../hooks/useN8nWebhook';
import { n8nOriginFromUrl } from '../../lib/analysis-models';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
const labelClass = 'text-sm';

// n8n "Predict Track Conditions" webhook for one meeting. It responds when the
// prediction is saved (~1 min), so the dialog can refresh in place.
function predictUrl(meetingId: string): string | null {
  const origin = n8nOriginFromUrl(import.meta.env.VITE_N8N_URL || import.meta.env.VITE_N8N_ANALYSIS_URL);
  return origin ? `${origin}/webhook/predict-track-conditions?meeting_id=${encodeURIComponent(meetingId)}` : null;
}

interface Props {
  meetingId: string;
  trackName: string;
  onClose: () => void;
}

export function TrackConditionsDialog({ meetingId, trackName, onClose }: Props) {
  const { data: actual, isLoading: loadingActual } = useTrackConditions(meetingId);
  const { data: predicted, isLoading: loadingPredicted } = usePredictedConditions(meetingId);
  const saveActual = useSaveTrackConditions(meetingId);
  const savePredicted = useSavePredictedConditions(meetingId);
  const { success, error } = useToast();
  const queryClient = useQueryClient();
  const { triggerWebhook, isLoading: predicting } = useN8nWebhook();

  const [draft, setDraft] = useState<TrackConditionsDraft>({});
  const [predictedDraft, setPredictedDraft] = useState<PredictedConditionsDraft>({});

  // Seed each form once its current values arrive.
  useEffect(() => {
    if (actual) setDraft(actual);
  }, [actual]);
  useEffect(() => {
    if (predicted) {
      setPredictedDraft({
        predicted_weather: predicted.predicted_weather,
        predicted_track_condition: predicted.predicted_track_condition,
        predicted_track_condition_number: predicted.predicted_track_condition_number,
        predicted_penetrometer: predicted.predicted_penetrometer,
      });
    }
  }, [predicted]);

  const set = (patch: TrackConditionsDraft) => setDraft((d) => ({ ...d, ...patch }));
  const setPredicted = (patch: PredictedConditionsDraft) => setPredictedDraft((d) => ({ ...d, ...patch }));

  const loading = loadingActual || loadingPredicted;
  const saving = saveActual.isPending || savePredicted.isPending;

  const handleSave = async () => {
    try {
      await saveActual.mutateAsync(draft);
      // Predicted is UPDATE-only, so only save it when the pipeline has produced a row.
      if (predicted) await savePredicted.mutateAsync(predictedDraft);
      success(`Track conditions updated for ${trackName}.`);
      onClose();
    } catch (err) {
      error(extractErrorMessage(err, 'Failed to save track conditions.'));
    }
  };

  const handleRepredict = async () => {
    const url = predictUrl(meetingId);
    if (!url) {
      error('n8n server URL is not configured (VITE_N8N_URL).');
      return;
    }
    if (await triggerWebhook(url, 'Track condition prediction')) {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      await queryClient.invalidateQueries({ queryKey: ['meeting-predicted-conditions', meetingId] });
      success(`New track condition prediction for ${trackName}.`);
    } else {
      error(`Track condition prediction failed for ${trackName}.`);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Track conditions — {trackName}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Actual going for this meeting. Feeds the readiness check and the analysis prompts.
          </Dialog.Description>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <label className={labelClass}>
                  Track condition
                  <input
                    className={inputClass}
                    value={draft.track_condition ?? ''}
                    onChange={(e) => set({ track_condition: e.target.value || null })}
                    placeholder="e.g. Soft, Dirt, Sand"
                  />
                </label>
                <label className={labelClass}>
                  Rating (1–10)
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className={inputClass}
                    value={draft.track_condition_number ?? ''}
                    onChange={(e) => set({ track_condition_number: e.target.value || null })}
                  />
                </label>
                <label className={labelClass}>
                  Weather
                  <input
                    className={inputClass}
                    value={draft.weather ?? ''}
                    onChange={(e) => set({ weather: e.target.value || null })}
                    placeholder="e.g. Fine, Overcast, Showery"
                  />
                </label>
                <label className={labelClass}>
                  Rail position
                  <input className={inputClass} value={draft.rail_position ?? ''} onChange={(e) => set({ rail_position: e.target.value || null })} placeholder="e.g. True, +3m" />
                </label>
                <label className={labelClass}>
                  Rainfall
                  <input className={inputClass} value={draft.rainfall ?? ''} onChange={(e) => set({ rainfall: e.target.value || null })} placeholder="e.g. 0mm" />
                </label>
                <label className={labelClass}>
                  Penetrometer
                  <input type="number" step="0.01" className={inputClass} value={draft.penetrometer ?? ''} onChange={(e) => set({ penetrometer: e.target.value || null })} />
                </label>
                <label className="col-span-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50/60 p-3 text-sm dark:border-red-900/50 dark:bg-red-950/20">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={!!draft.abandoned}
                    onChange={(e) => set({ abandoned: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Abandoned</span>
                    <span className="block text-xs text-muted-foreground">
                      The meeting won't race. Members see it marked abandoned and its races drop out of upcoming tips.
                    </span>
                  </span>
                </label>
                <label className="col-span-2 text-sm">
                  Comment
                  <textarea className={`${inputClass} min-h-[70px]`} value={draft.comment ?? ''} onChange={(e) => set({ comment: e.target.value || null })} />
                </label>
              </div>

              <div className="mt-5 border-t border-border pt-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">AI predicted conditions</h3>
                  {predicted && (
                    <span className="text-xs text-muted-foreground">
                      {predicted.ai_model ?? 'model'}
                      {predicted.prediction_confidence && ` · ${predicted.prediction_confidence} confidence`}
                    </span>
                  )}
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleRepredict} disabled={predicting}>
                  {predicting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {predicting ? 'Predicting… (about a minute)' : predicted ? 'Re-predict conditions' : 'Predict conditions'}
                </Button>

                {predicted?.ai_reasoning && (
                  <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Why the model predicts this</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{predicted.ai_reasoning}</p>
                  </div>
                )}

                {predicted ? (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <label className={labelClass}>
                      Predicted condition
                      <input
                        className={inputClass}
                        value={predictedDraft.predicted_track_condition ?? ''}
                        onChange={(e) => setPredicted({ predicted_track_condition: e.target.value || null })}
                        placeholder="e.g. Soft, Dirt, Sand"
                      />
                    </label>
                    <label className={labelClass}>
                      Predicted rating (1–10)
                      <input
                        type="number"
                        min={1}
                        max={10}
                        className={inputClass}
                        value={predictedDraft.predicted_track_condition_number ?? ''}
                        onChange={(e) => setPredicted({ predicted_track_condition_number: e.target.value || null })}
                      />
                    </label>
                    <label className={labelClass}>
                      Predicted weather
                      <input
                        className={inputClass}
                        value={predictedDraft.predicted_weather ?? ''}
                        onChange={(e) => setPredicted({ predicted_weather: e.target.value || null })}
                        placeholder="e.g. Fine, Overcast, Showery"
                      />
                    </label>
                    <label className={labelClass}>
                      Predicted penetrometer
                      <input
                        type="number"
                        step="0.01"
                        className={inputClass}
                        value={predictedDraft.predicted_penetrometer ?? ''}
                        onChange={(e) => setPredicted({ predicted_penetrometer: e.target.value || null })}
                      />
                    </label>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No AI prediction generated for this meeting yet.</p>
                )}
              </div>
            </>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save conditions
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
